import React, { useEffect, useState, useCallback } from "react";
import { Container, Row, Col, Card, CardHeader, CardBody, Button, Modal, ModalBody, ModalHeader, Input, Label, } from "@/Components/ui";
import BreadCrumb from "@/Components/Common/BreadCrumb";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import { BACKEND_HOST, getDataFromUrl, postData } from "@/api";
import moment from "moment";
import { dialogService } from "@/utils/dialogService";
import CommonDatePicker from "@/Components/Common/CommonDatePicker";
interface Announcement {
    id: number;
    title: string;
    body: string;
    image_url?: string;
    link?: string;
    android_link?: string;
    ios_link?: string;
    pub_date: string;
    end_date: string;
    show_on_app: boolean;
    all_location: boolean;
}
interface FormData {
    title: string;
    body: string;
    image: File | null;
    link: string;
    android_link: string;
    ios_link: string;
    pub_date: string;
    end_date: string;
    show_on_app: boolean;
    all_location: boolean;
}
const defaultFormData: FormData = {
    title: "",
    body: "",
    image: null,
    link: "",
    android_link: "",
    ios_link: "",
    pub_date: moment().format("YYYY-MM-DD"),
    end_date: moment().add(7, "days").format("YYYY-MM-DD"),
    show_on_app: false,
    all_location: false,
};
const AnnounceList = () => {
    const navigate = useNavigate();
    const [announcements, setAnnouncements] = useState<Announcement[]>([]);
    const [loading, setLoading] = useState(true);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
    const [formData, setFormData] = useState<FormData>(defaultFormData);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const fetchAnnouncements = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getDataFromUrl(`${BACKEND_HOST}/api/v2/notices/announce/`);
            setAnnouncements(response.results || response || []);
        }
        catch (error) {
            console.error("Failed to fetch announcements:", error);
            toast.error("배너를 불러오는데 실패했습니다.");
        }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        fetchAnnouncements();
    }, [fetchAnnouncements]);
    const openCreateForm = () => {
        setEditingAnnouncement(null);
        setFormData(defaultFormData);
        setImagePreview(null);
        setIsFormOpen(true);
    };
    const openEditForm = (announcement: Announcement) => {
        setEditingAnnouncement(announcement);
        setFormData({
            title: announcement.title,
            body: announcement.body || "",
            image: null,
            link: announcement.link || "",
            android_link: announcement.android_link || "",
            ios_link: announcement.ios_link || "",
            pub_date: moment(announcement.pub_date).format("YYYY-MM-DD"),
            end_date: moment(announcement.end_date).format("YYYY-MM-DD"),
            show_on_app: announcement.show_on_app,
            all_location: announcement.all_location,
        });
        setImagePreview(announcement.image_url || null);
        setIsFormOpen(true);
    };
    const closeForm = () => {
        setIsFormOpen(false);
        setEditingAnnouncement(null);
        setFormData(defaultFormData);
        setImagePreview(null);
    };
    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setFormData((prev) => ({ ...prev, image: file }));
            const reader = new FileReader();
            reader.onload = () => setImagePreview(reader.result as string);
            reader.readAsDataURL(file);
        }
    };
    const handleSubmit = async () => {
        if (!formData.title.trim()) {
            toast.error("제목을 입력해주세요.");
            return;
        }
        try {
            const submitData = new FormData();
            submitData.append("title", formData.title);
            submitData.append("body", formData.body);
            submitData.append("link", formData.link);
            submitData.append("android_link", formData.android_link);
            submitData.append("ios_link", formData.ios_link);
            submitData.append("pub_date", formData.pub_date);
            submitData.append("end_date", formData.end_date);
            submitData.append("show_on_app", String(formData.show_on_app));
            submitData.append("all_location", String(formData.all_location));
            if (formData.image) {
                submitData.append("image", formData.image);
            }
            const url = editingAnnouncement
                ? `${BACKEND_HOST}/api/v2/notices/announce/${editingAnnouncement.id}/`
                : `${BACKEND_HOST}/api/v2/notices/announce/`;
            const method = editingAnnouncement ? "PATCH" : "POST";
            await postData(url, submitData, method);
            toast.success(editingAnnouncement ? "배너가 수정되었습니다." : "배너가 등록되었습니다.");
            closeForm();
            fetchAnnouncements();
        }
        catch (error) {
            console.error("Failed to save announcement:", error);
            toast.error("저장에 실패했습니다.");
        }
    };
    const handleDelete = async (id: number) => {
        if (!await dialogService.confirm("이 배너를 삭제하시겠습니까?"))
            return;
        try {
            await postData(`${BACKEND_HOST}/api/v2/notices/announce/${id}/`, {}, "DELETE");
            toast.success("배너가 삭제되었습니다.");
            fetchAnnouncements();
        }
        catch (error) {
            console.error("Failed to delete announcement:", error);
            toast.error("삭제에 실패했습니다.");
        }
    };
    const isActive = (announcement: Announcement) => {
        const now = moment();
        return now.isBetween(announcement.pub_date, announcement.end_date, "day", "[]");
    };
    return (<React.Fragment>
      <Toaster position="top-center"/>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="배너" pageTitle="배너 관리"/>
          <Card>
            <CardHeader className="border-0">
              <Row className="g-4 align-items-center">
                <div className="col-sm">
                  <h5 className="card-title mb-0">배너 목록</h5>
                </div>
                <div className="col-sm-auto">
                  <Button color="primary" onClick={openCreateForm}>
                    <i className="ri-add-line align-bottom me-1"></i>
                    새 배너 작성
                  </Button>
                </div>
              </Row>
            </CardHeader>
            <CardBody>
              {loading ? (<div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>) : announcements.length === 0 ? (<div className="text-center py-4 text-muted">
                  등록된 배너가 없습니다.
                </div>) : (<div className="w-full overflow-x-auto">
                  <table className="w-full mb-0 text-sm border-y border-slate-200">
                    <thead className="bg-slate-50 border-b border-slate-200 text-slate-500">
                      <tr>
                        <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap" style={{ width: "80px" }}>이미지</th>
                        <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">제목</th>
                        <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap" style={{ width: "100px" }}>상태</th>
                        <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap" style={{ width: "180px" }}>기간</th>
                        <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap" style={{ width: "80px" }}>앱표시</th>
                        <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap" style={{ width: "120px" }}>관리</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {announcements.map((announcement) => (<tr key={announcement.id} className="hover:bg-slate-50/80 transition-colors">
                          <td className="p-4 align-middle whitespace-nowrap text-slate-700">
                            {announcement.image_url ? (<img src={announcement.image_url} alt={announcement.title} style={{ width: 60, height: 40, objectFit: "cover", borderRadius: 4 }}/>) : (<div style={{
                        width: 60,
                        height: 40,
                        backgroundColor: "#f0f0f0",
                        borderRadius: 4,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}>
                                <i className="ri-image-line text-muted"></i>
                              </div>)}
                          </td>
                          <td className="p-4 align-middle whitespace-nowrap text-slate-700">
                            <span className="text-primary font-medium" onClick={() => openEditForm(announcement)} style={{ cursor: "pointer" }}>
                              {announcement.title}
                            </span>
                          </td>
                          <td className="p-4 align-middle whitespace-nowrap text-slate-700">
                            <span className={`badge ${isActive(announcement) ? "bg-success" : "bg-secondary"}`}>
                              {isActive(announcement) ? "진행중" : "종료"}
                            </span>
                          </td>
                          <td className="p-4 align-middle whitespace-nowrap text-slate-500" style={{ fontSize: "12px" }}>
                            {moment(announcement.pub_date).format("YYYY-MM-DD")} ~{" "}
                            {moment(announcement.end_date).format("YYYY-MM-DD")}
                          </td>
                          <td className="p-4 align-middle whitespace-nowrap text-slate-700">
                            <span className={`badge ${announcement.show_on_app ? "bg-info" : "bg-light text-dark"}`}>
                              {announcement.show_on_app ? "표시" : "-"}
                            </span>
                          </td>
                          <td className="p-4 align-middle whitespace-nowrap text-slate-700">
                            <Button color="soft-primary" size="sm" className="me-1" onClick={() => openEditForm(announcement)}>
                              수정
                            </Button>
                            <Button color="soft-danger" size="sm" onClick={() => handleDelete(announcement.id)}>
                              삭제
                            </Button>
                          </td>
                        </tr>))}
                    </tbody>
                  </table>
                </div>)}
            </CardBody>
          </Card>
        </Container>
      </div>

      <Modal isOpen={isFormOpen} onClose={closeForm} centered size="lg">
        <ModalHeader toggle={closeForm}>
          {editingAnnouncement ? "배너 수정" : "새 배너 작성"}
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <Label className="form-label">제목 *</Label>
            <Input type="text" placeholder="배너 제목을 입력하세요" value={formData.title} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData((prev) => ({ ...prev, title: e.target.value }))}/>
          </div>

          <div className="mb-3">
            <Label className="form-label">이미지</Label>
            <Input type="file" accept="image/*" onChange={handleImageChange}/>
            {imagePreview && (<div className="mt-2">
                <img src={imagePreview} alt="Preview" style={{ maxWidth: 200, borderRadius: 4 }}/>
              </div>)}
          </div>

          <div className="mb-3">
            <Label className="form-label">내용</Label>
            <textarea className="form-control" rows={4} placeholder="배너 내용을 입력하세요" value={formData.body} onChange={(e) => setFormData((prev) => ({ ...prev, body: e.target.value }))}/>
          </div>

          <Row className="mb-3">
            <Col md={6}>
              <Label className="form-label">시작일</Label>
              <CommonDatePicker value={formData.pub_date} clearable onChangeValue={(value) => setFormData((prev) => ({ ...prev, pub_date: value }))}/>
            </Col>
            <Col md={6}>
              <Label className="form-label">종료일</Label>
              <CommonDatePicker value={formData.end_date} clearable onChangeValue={(value) => setFormData((prev) => ({ ...prev, end_date: value }))}/>
            </Col>
          </Row>

          <div className="mb-3">
            <Label className="form-label">링크 (웹)</Label>
            <Input type="url" placeholder="https://..." value={formData.link} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData((prev) => ({ ...prev, link: e.target.value }))}/>
          </div>

          <Row className="mb-3">
            <Col md={6}>
              <Label className="form-label">Android 링크</Label>
              <Input type="url" placeholder="https://..." value={formData.android_link} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData((prev) => ({ ...prev, android_link: e.target.value }))}/>
            </Col>
            <Col md={6}>
              <Label className="form-label">iOS 링크</Label>
              <Input type="url" placeholder="https://..." value={formData.ios_link} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData((prev) => ({ ...prev, ios_link: e.target.value }))}/>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <div className="form-check">
                <input className="form-check-input" type="checkbox" id="show_on_app" checked={formData.show_on_app} onChange={(e) => setFormData((prev) => ({ ...prev, show_on_app: e.target.checked }))}/>
                <label className="form-check-label" htmlFor="show_on_app">
                  앱에서 표시
                </label>
              </div>
            </Col>
            <Col md={6}>
              <div className="form-check">
                <input className="form-check-input" type="checkbox" id="all_location" checked={formData.all_location} onChange={(e) => setFormData((prev) => ({ ...prev, all_location: e.target.checked }))}/>
                <label className="form-check-label" htmlFor="all_location">
                  전체 지점 표시
                </label>
              </div>
            </Col>
          </Row>

          <div className="d-flex justify-content-end gap-2">
            <Button color="secondary" onClick={closeForm}>
              취소
            </Button>
            <Button color="primary" onClick={handleSubmit}>
              {editingAnnouncement ? "수정" : "등록"}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>);
};
export default AnnounceList;
